/**
 * Created by Amit
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {BitmapWinCountUp} from "@nolimitcity/slot-game/bin/game/winpresentation/countup/BitmapWinCountUp";
import {Elastic, Power3, Timeline, TimelineLite, TweenLite, TimelineMax, Linear, Power2, Back} from "gsap";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {SpacierArcadeGameConfig} from "../../SpacierArcadeGameConfig";
import {SpacierArcadeWildSymbol} from "../../symbols/SpacierArcadeWildSymbol";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/BitmapTextStyleOptions";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SATextStyles} from "../../../config/SATextStyles";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {SpacierArcadeSoundConfig} from "../../SpacierArcadeSoundConfig";

export class SpacierArcadeInitialWinCountUp extends BitmapWinCountUp {
    private _winWays:PIXI.Text;
    private _isCountUpSoundPlaying: boolean = false;
    private _hasCountUpEndSoundPlayed: boolean = false;
    private _flair:PIXI.Sprite;
    private _multiplierText:PIXI.Text;

    constructor() {
        super({
            font: {
                name: "mw_font",
                size: 15
            },
            align: "center",
            tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
    }

    protected initAnimations():void{
        super.initAnimations();
        this.position.set(GameConfig.instance.REEL_AREA_WIDTH * 0.5 + GameConfig.instance.REEL_AREA_POS_X, GameConfig.instance.REEL_AREA_HEIGHT * 0.48 +  GameConfig.instance.REEL_AREA_POS_Y);
        StageManager.getLayer(SpacierArcadeGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this);
        this._multiplierText = new PIXI.Text(Translation.translate("X1"), SATextStyles.MULTI_TEXT);
        this._multiplierText.anchor = new PIXI.Point(0.5, 0.8);
        this._multiplierText.position.set(0, -this._countUp.height * 0.6);
        this._multiplierText.scale.set(0, 0);
        this._multiplierText.alpha = 0;
        this._countUpContainer.addChild(this._multiplierText);


        this._winWays = new PIXI.Text(Translation.translate("243 Ways Won"), SATextStyles.WIN_WAYS_TEXT);
        this._winWays.anchor.set(0.5, 0.5);
        this._winWays.position.set(0, 80);
        this._winWays.alpha = 0;

        this._countUpContainer.addChild((this._winWays));


        this._flair = new PIXI.Sprite(GameResources.getTextures("winamount_flair")[0]);
        this._flair.anchor.set(0.5);
        this._flair.scale.set(0);
        this._flair.position.set(GameConfig.instance.REEL_AREA_WIDTH * 0.5 + GameConfig.instance.REEL_AREA_POS_X, GameConfig.instance.REEL_AREA_HEIGHT * 0.5 +  GameConfig.instance.REEL_AREA_POS_Y);
        this._flair.blendMode = PIXI.BLEND_MODES.ADD;
        StageManager.getLayer(SpacierArcadeGameConfig.instance.LAYERS.BIG_WIN.name).addChild(this._flair);
    }

    public updateWinWays(winWays:number):void{
        let staticText:string = winWays > 1 ? " Ways Won" : " Way Won";
        this._winWays.text = Translation.translate( winWays + staticText);
    }

    private getShowMultiplier(multiplier:number, duration:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(multiplier < 2) {
            return tl;
        }

        tl.add([
            () => this._multiplierText.text = "X" + multiplier,
            new TweenLite(this._multiplierText, 0.01, {alpha : 1, ease : Linear.easeNone}),
            new TweenLite(this._multiplierText.scale, 0.01, {x : 1, y : 1, ease : Power2.easeIn})
        ]);
        return tl;
    }

    private getHideMultiplier(duration:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            new TweenLite(this._multiplierText, duration * 0.3, {alpha : 0, ease : Linear.easeNone}),
            new TweenLite(this._multiplierText.scale, duration, {x : 0, y : 0, ease : Back.easeOut})
        ]);


        return tl;
    }

    public getMultiShakeAnimation():TimelineLite{
        let a  = new TimelineLite();
        a.to(this._countUp.scale, 0.2, {x: 0.5, y: 0.5});
        for (let i:number = 0; i < 5; i++) {
            let start = "shake" + i;
            let duration = this.random(0.09, 0.1);
            let scale = this.random(0.4, 0.5);

            a.to(this._countUp.scale, duration / 2, {
                x: scale,
                y: scale,
                repeat: 1,
                yoyo: true
            }, start);
        }
        a.to(this._countUp.scale, 0.15, {x: 1.1, y: 1.1});
        a.to(this._countUp.scale, 0.08, {x: 1, y: 1});
        return a;
    }

    private random(min:number, max:number):number {
        return min + (max - min) * Math.random();
    }

    public getSmallCountUpAnimation(totalWinWaysWon:number, from: number, to: number, duration: number, multiplier: number, winRatioConfig:IWinRatioConfig | null): Timeline {
        this.updateWinWays(totalWinWaysWon);
        const divDelta:number = winRatioConfig!.type === WinRatioType.LARGE? 8.4 : 16.5;
        const timeline: TimelineLite = new TimelineLite();
        const scaleInit:number = multiplier > 1 ? 1 : 0.85;
        this._countUp.getCountUpAnimation(from, to, {duration: 0});
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUpContainer.scale.set(1, 1);
                this._countUp.getCountUpAnimation(from, to, {duration: 0, ease: Power3.easeInOut});
            },
            TweenLite.fromTo(this._countUpContainer.scale, 1,
                {x : scaleInit, y : scaleInit},
                {
                    x : scaleInit,
                    y : scaleInit,
                    ease : Elastic.easeOut.config(2, 1)
                })
        ], 0);
        if(totalWinWaysWon > 0) {
            timeline.add([
                new TweenLite(this._winWays.position, 0, {x: divDelta }),
                new TweenLite(this._winWays, 0.1, {alpha : 1})
            ], 0);
        }
        timeline.add(() => {
            this._countUp.alpha = 1;
            this._countUp.visible = true;
        },  0.01);
        return timeline;
    }

    public getCountUpAnimation(totalWinWaysWon:number, from: number, to: number, duration: number, multiplier: number, winRatioConfig:IWinRatioConfig | null): Timeline {
        this.updateWinWays(totalWinWaysWon);
        const divDelta:number = winRatioConfig!.type === WinRatioType.LARGE? 8.4 : 16.5;
        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            this._countUp.getCountUpAnimation(from, to, {duration: duration, ease: Power3.easeInOut}),
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUpContainer.scale.set(1, 1);
                if (duration > 0.01) {
                    this._isCountUpSoundPlaying = true;
                }
            },
            TweenLite.to(this._flair.scale, 0.3, {x: 3, y : 1.5}),
            TweenLite.fromTo(this._countUpContainer.scale, 1,
                {x : 0.85, y : 0.85},
                {
                    x : 1,
                    y : 1,
                    ease : Elastic.easeOut.config(2, 1)
            }),
            () => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.COUNTUP, 100),
        ]);
        timeline.add(() => SlotGame.sound.stopEffect(SpacierArcadeSoundConfig.instance.COUNTUP));
        timeline.add(() => {
            this._countUp.alpha = 1;
            this._countUp.visible = true;
        },  0.01);
        if(totalWinWaysWon > 0) {
            timeline.add([
                new TweenLite(this._winWays.position, 0, {x: divDelta }),
                new TweenLite(this._winWays, 0.2, {alpha : 1})
            ], `-=0.1`);
        }
        timeline.add(
            TweenLite.to(this._flair.scale, 0.2, {x: 0, y: 0})
        , `-=0.1`);
        return timeline;
    }

    public getMultiplierCountUpAnimation(from: number, to: number, duration: number, multiplier: number, winRatioConfig:IWinRatioConfig | null, winBelowStake: boolean): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        if (multiplier > 1) {
            for (let m: number = 2; m <= multiplier; m++) {
                let showNumber: number = from;
                let scale: number = this.getScale(m);

                timeline.add(this.getShowMultiplier(m, 0.01));
                timeline.add(() => {
                    scale = this.getScale(m);
                    this._countUp.getCountUpAnimation(from * m, from * m, {duration: 0});
                    TweenLite.to(this._countUpContainer.scale, 0.3,
                    {
                        x: scale,
                        y: scale,
                        ease: Elastic.easeOut.config(2, 1)
                    });
                    // #353
                    if (!winBelowStake) {
                        if (m !== multiplier) {
                            SlotGame.sound.playEffect("countUpMultiplier" + (m % 2 === 0 ? 2 : 1));
                        } else {
                            SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.COUNTUP_ENDS);
                        }
                    }
                });
                timeline.add(TweenLite.to(this, 0.47, {}));
            }
        }
        return timeline;
    }

    public abort(endValue: number, isAborted: boolean, multiplier: number): Timeline | null {
        return super.abort(endValue, isAborted, multiplier);
    }

    public getEndingAnimation(multiplier:number, totalWinWaysWon:number, endValue: number, winRatioConfig:IWinRatioConfig | null, playWinWays:boolean = true): Timeline {

        this.updateWinWays(totalWinWaysWon);
        this._flair.scale.set(0);
        const divDelta:number = winRatioConfig!.type === WinRatioType.LARGE? 8.4 : 16.5;
        const smallWin:boolean =  winRatioConfig!.ratio < 3;
        const stayValue:number = (smallWin && multiplier <= 1) ? 0 : 0.8;

        const timeline: TimelineLite = new TimelineLite({
            onComplete: () => {
                this._isCountUpSoundPlaying = false;
                this._hasCountUpEndSoundPlayed = false;
            }
        });
        if(!smallWin && multiplier === 1) {
            timeline.add([
                () => SlotGame.sound.stopEffect(SpacierArcadeSoundConfig.instance.COUNTUP),
                () => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.COUNTUP_ENDS)
            ], 0);
        }
        timeline.add([
            () => {
                SlotGame.sound.stopEffect(SpacierArcadeSoundConfig.instance.COUNTUP);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                SlotGame.winFieldController.showWinField();
            },
            this._countUp.getCountUpAnimation(endValue, endValue, {duration: 0.01})
        ], 0);
        if(totalWinWaysWon > 0) {
            timeline.add([
                new TweenLite(this._winWays.position, 0, {x: divDelta}),
                new TweenLite(this._winWays, 0.1, {alpha: 1})
            ], 0);
        }
        timeline.add( this.stay(stayValue));
        timeline.add([
            TweenLite.to(this._countUp, 0.15, {alpha: 0}),
            new TweenLite(this._winWays, 0.15, {alpha : 0}),
            this.getHideMultiplier(0.15)
        ], `+=0.1`); // stay a while after count up.
        timeline.add(() => {
            this._countUp.alpha = 0;
            this._winWays.alpha = 0;
            this.getHideMultiplier(0)
        });
        return timeline;
    }

    protected getAbortAnimation(endValue: number, multiplier: number): Timeline {
        const timeline: TimelineLite = new TimelineLite();
        this._flair.scale.set(0);
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
            },
            () => SlotGame.winFieldController.showWinField(),
            () => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.COUNTUP_ENDS),
            this._countUp.getCountUpAnimation(endValue, endValue, {duration: 0.01}),
        ]);
        if (multiplier > 1) {
            const scale: number = this.getScale(multiplier);
            timeline.add(TweenLite.to(this._countUpContainer.scale, 0.2, {
                x: scale,
                y: scale,
                ease: Elastic.easeOut.config(2, 1)
            }), 0)
        }

        return timeline;
    }

    public stay(duration:number):Timeline {
        const timeline:TimelineLite = new TimelineLite();
        // timeline.add(()=> EventHandler.dispatchEvent(new GameEvent(SoundEvent.PLAY_SOUND_EFFECT, TombstoneSoundConfig.instance.COUNT_UP_BACK)));
        timeline.add(TweenLite.to(this._countUpContainer.scale, duration, {
            x : 0.85,
            y : 0.85,
            ease : Elastic.easeOut.config(1.25, 0.75)
        }));
        return timeline;
    }

    private getScale(multiplier: number): number {
        const scale:number = 1 + (multiplier - 1) * 0.1
        return 1 + (multiplier - 1) * 0.1;
    }
}